import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { getRouteForNavigation } from "@/utils/routeAccess";
import {
  Heart,
  TrendingUp,
  Users,
  Upload,
  AlertCircle,
  Plus,
  Clock,
  Globe,
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import StatsCard from "../components/dashboard/StatsCard";
import RecentTests from "../components/dashboard/RecentTests";
import RevenueChart from "../components/dashboard/RevenueChart";
import QuickActions from "../components/dashboard/QuickActions";
import PracticeMetrics from "../components/dashboard/PracticeMetrics";
import BillingWrapper from "../components/BillingWrapper";

export default function DoctorDashboard() {
  const { user } = useAuth();

  // Get practice information
  const { data: practice, isLoading: practiceLoading } = useQuery({
    queryKey: ["practice"],
    queryFn: () => apiClient.get("/api/settings/practice"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get blood tests for the practice
  const { data: bloodTests, isLoading: bloodTestsLoading } = useQuery({
    queryKey: ["bloodTests"],
    queryFn: () => apiClient.get("/api/blood-tests"),
  });

  // Get patients for the practice
  const { data: patients, isLoading: patientsLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
  });

  // Get bookings for the practice
  const { data: bookings, isLoading: bookingsLoading } = useQuery({
    queryKey: ["bookings"],
    queryFn: () => apiClient.get("/api/bookings"),
  });

  const isLoading = bloodTestsLoading || patientsLoading || bookingsLoading;

  // Calculate stats from real data
  const pendingTests =
    Array.isArray(bloodTests)
      ? bloodTests.filter(
          (test) => !test.results || Object.keys(test.results).length === 0
        ).length
      : 0;

  const totalPatients = Array.isArray(patients) ? patients.length : 0;

  const today = format(new Date(), "yyyy-MM-dd");
  const appointmentsTodayCount =
    Array.isArray(bookings) 
      ? bookings.filter(
          (booking) => booking.date === today && booking.status === "scheduled"
        ).length 
      : 0;

  const firstDayOfMonth = format(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    "yyyy-MM-dd"
  );
  const newPatientsCount =
    Array.isArray(patients)
      ? patients.filter((p) => new Date(p.createdAt) >= new Date(firstDayOfMonth))
          .length
      : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8" data-testid="doctor-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Health Dashboard
          </h1>
          <p className="text-slate-600">
            Welcome back
            {user?.firstName
              ? `, ${user.userType === "doctor" ? "Dr." : ""} ${user.firstName}`
              : ""}
            . Here&apos;s your practice overview.
          </p>
          {practice && practice.timezone && (
            <div className="flex items-center gap-2 mt-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200 max-w-fit">
              <Globe className="w-4 h-4" />
              <span>
                <strong>{practice.name || 'Your practice'}</strong> operates in{' '}
                <strong>{practice.timezone}</strong> timezone
              </span>
              <Clock className="w-3 h-3 ml-1 opacity-70" />
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <Link to={getRouteForNavigation("Blood Tests")}>
            <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg">
              <Upload className="w-4 h-4 mr-2" />
              Upload Test
            </Button>
          </Link>
          <Link to={getRouteForNavigation("Clinical Notes")}>
            <Button
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Note
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Patients"
          value={totalPatients}
          icon={Users}
          trend={
            newPatientsCount > 0
              ? `+${newPatientsCount} this month`
              : "No new patients"
          }
          color="blue"
        />
        <StatsCard
          title="Blood Tests"
          value={bloodTests?.length || 0}
          icon={Heart}
          trend={`${pendingTests} pending`}
          color="red"
        />
        <BillingWrapper enabled={false}>
          <StatsCard
            title="Revenue"
            value="$0"
            icon={TrendingUp}
            color="green"
          />
        </BillingWrapper>
        <StatsCard
          title="Today's Appointments"
          value={appointmentsTodayCount}
          icon={AlertCircle}
          urgent={appointmentsTodayCount > 5}
          color="orange"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-8">
          <RecentTests tests={bloodTests || []} isLoading={isLoading} />
          <BillingWrapper enabled={false}>
            <RevenueChart billing={[]} isLoading={isLoading} />
          </BillingWrapper>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-8">
          <PracticeMetrics
            totalPatients={totalPatients}
            newPatientsCount={newPatientsCount}
            appointmentsTodayCount={appointmentsTodayCount}
          />

          <QuickActions />
        </div>
      </div>
    </div>
  );
}
